/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { deviceStatusColor } from './materials'
import { defaultMountHeight, normalizeMountType } from './apMounts'
import { computeHeatmapGrid, heatmapColor, rfMetricsAtPoint, rssiAtPoint, type HeatmapCell } from './rfSimulation'
import { RF_GRID_COLS, RF_GRID_ROWS } from './rfGrid'
import { AP_DELETE_BADGE } from './hitTest'
import type {
  FloorAlarmDot,
  FloorBackgroundCrop,
  FloorClientDot,
  FloorDevice,
  FloorGeometry,
  FloorObstacle,
  FloorRoom,
  FloorWall,
  HeatmapLayer,
  PlacedAp,
} from './types'
import { formatObstacleSize, obstacleDrawStyle, resolveObstacleFootprint } from './obstacles'
import { formatWallSize, resolveWallDimensions, wallDrawStyle } from './walls'
import { computeFloorDrawLayout, FLOOR_PAD, pctToCanvas } from './floorLayout'

export { FLOOR_PAD } from './floorLayout'

type Ctx = CanvasRenderingContext2D

export function drawSnapGrid(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  gridPct: number,
  dark: boolean,
  onPlan = false,
) {
  const layout = computeFloorDrawLayout(lw, lh, widthM, heightM)
  ctx.save()
  ctx.strokeStyle = onPlan
    ? dark
      ? 'rgba(148,163,184,0.28)'
      : 'rgba(100,116,139,0.26)'
    : dark
      ? 'rgba(148,163,184,0.18)'
      : 'rgba(100,116,139,0.16)'
  ctx.lineWidth = 1
  for (let i = 1; i * gridPct < 100; i++) {
    const p = Math.round(i * gridPct * 1000) / 1000
    const x = layout.x + (p / 100) * layout.w
    const y = layout.y + (p / 100) * layout.h
    ctx.beginPath()
    ctx.moveTo(x, layout.y)
    ctx.lineTo(x, layout.y + layout.h)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(layout.x, y)
    ctx.lineTo(layout.x + layout.w, y)
    ctx.stroke()
  }
  ctx.restore()
}

export function drawFloorBase(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  dark: boolean,
  bgImage: HTMLImageElement | null,
  crop?: FloorBackgroundCrop | null,
) {
  const layout = computeFloorDrawLayout(lw, lh, widthM, heightM)
  ctx.fillStyle = dark ? '#0f172a' : '#cbd5e1'
  ctx.fillRect(layout.pad, layout.pad, layout.boxW, layout.boxH)

  if (bgImage) {
    const t = crop?.top ?? 0
    const b = crop?.bottom ?? 0
    const l = crop?.left ?? 0
    const r = crop?.right ?? 0
    const sx = bgImage.width * l
    const sy = bgImage.height * t
    const sw = bgImage.width * (1 - l - r)
    const sh = bgImage.height * (1 - t - b)
    ctx.drawImage(bgImage, sx, sy, sw, sh, layout.x, layout.y, layout.w, layout.h)
  } else {
    ctx.fillStyle = dark ? '#111827' : '#f8fafc'
    ctx.fillRect(layout.x, layout.y, layout.w, layout.h)
  }

  ctx.strokeStyle = dark ? 'rgba(148,163,184,0.5)' : 'rgba(71,85,105,0.45)'
  ctx.lineWidth = 2
  ctx.strokeRect(layout.x, layout.y, layout.w, layout.h)
  ctx.fillStyle = dark ? '#94a3b8' : '#64748b'
  ctx.font = '600 11px Inter, system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(`${widthM} m × ${heightM} m`, layout.x + layout.w / 2, layout.y + layout.h + 18)
  ctx.textAlign = 'left'
}

export function drawRooms(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  rooms: FloorRoom[],
  dark: boolean,
  selectedId?: string | null,
) {
  for (const room of rooms) {
    const p = pctToCanvas(room.x, room.y, lw, lh, widthM, heightM)
    const w = (room.w / 100) * p.fw
    const h = (room.h / 100) * p.fh
    const selected = room.id === selectedId
    const isRequirement = room.coverageRequirement != null
    ctx.fillStyle = selected
      ? 'rgba(37,99,235,0.14)'
      : isRequirement
        ? 'rgba(16,185,129,0.1)'
        : dark
          ? 'rgba(59,130,246,0.08)'
          : 'rgba(59,130,246,0.06)'
    ctx.fillRect(p.x, p.y, w, h)
    ctx.strokeStyle = selected
      ? '#2563eb'
      : isRequirement
        ? '#10b981'
        : dark
          ? 'rgba(96,165,250,0.45)'
          : 'rgba(59,130,246,0.35)'
    ctx.lineWidth = selected ? 2.5 : isRequirement ? 2 : 1.5
    if (isRequirement) ctx.setLineDash([6, 4])
    ctx.strokeRect(p.x, p.y, w, h)
    ctx.setLineDash([])
    ctx.fillStyle = dark ? 'rgba(226,232,240,0.75)' : 'rgba(30,41,59,0.65)'
    ctx.font = '600 11px Inter, system-ui, sans-serif'
    ctx.fillText(room.name, p.x + 8, p.y + 18)
    if (isRequirement) {
      const minDbm = room.coverageRequirement?.minRssiDbm ?? -65
      ctx.fillStyle = '#059669'
      ctx.font = '500 9px Inter, system-ui, sans-serif'
      ctx.fillText(`≥ ${minDbm} dBm`, p.x + 8, p.y + 32)
    }
  }
}

export function drawWalls(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  walls: FloorWall[],
  preview?: FloorWall | null,
  selectedId?: string | null,
) {
  const all = preview ? [...walls, preview] : walls
  for (const w of all) {
    const a = pctToCanvas(w.x1, w.y1, lw, lh, widthM, heightM)
    const b = pctToCanvas(w.x2, w.y2, lw, lh, widthM, heightM)
    const isPreview = preview && w === preview
    const selected = w.id === selectedId
    const style = wallDrawStyle(w.material)
    const baseWidth = Math.max(3, resolveWallDimensions(w).thicknessM * 10)
    const lineWidth = (isPreview ? 3 : selected ? 4 : baseWidth) * style.widthMul

    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(b.x, b.y)
    if (!isPreview && !selected) {
      ctx.strokeStyle = style.glow
      ctx.lineWidth = lineWidth + 4
      ctx.lineCap = 'round'
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
    }
    ctx.strokeStyle = isPreview || selected ? '#2563eb' : style.stroke
    ctx.lineWidth = lineWidth
    ctx.lineCap = 'round'
    if (isPreview) ctx.setLineDash([6, 4])
    ctx.stroke()
    ctx.setLineDash([])
    if (selected && !isPreview) {
      const mx = (a.x + b.x) / 2
      const my = (a.y + b.y) / 2
      ctx.fillStyle = '#93c5fd'
      ctx.font = '8px Inter, system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(formatWallSize(w), mx, my - 6)
      ctx.textAlign = 'left'
    }
  }
}

export function drawObstacles(
  ctx: Ctx,
  lw: number,
  lh: number,
  obstacles: FloorObstacle[],
  selectedId?: string | null,
  floorWidthM = 100,
  floorHeightM = 80,
) {
  for (const o of obstacles) {
    const { w, h } = resolveObstacleFootprint(o, floorWidthM, floorHeightM)
    const c = pctToCanvas(o.x, o.y, lw, lh, floorWidthM, floorHeightM)
    const pw = (w / 100) * c.fw
    const ph = (h / 100) * c.fh
    const selected = o.id === selectedId
    const style = obstacleDrawStyle(o.type)
    ctx.fillStyle = selected ? 'rgba(37,99,235,0.32)' : style.fill
    ctx.fillRect(c.x - pw / 2, c.y - ph / 2, pw, ph)
    ctx.strokeStyle = selected ? '#2563eb' : style.stroke
    ctx.lineWidth = selected ? 2.5 : 2
    ctx.strokeRect(c.x - pw / 2, c.y - ph / 2, pw, ph)
    ctx.fillStyle = selected ? '#93c5fd' : style.label
    ctx.font = '9px Inter, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText(o.type.replace(/_/g, ' '), c.x, c.y - 2)
    ctx.font = '8px Inter, system-ui, sans-serif'
    ctx.fillText(formatObstacleSize(o, floorWidthM, floorHeightM), c.x, c.y + 8)
    ctx.textAlign = 'left'
  }
}

/** Typical usable cell radius in meters (planning guide — not FSPL max range). */
function apTypicalCellRadiusM(bandGHz: number, txDbm: number): number {
  const bandAdj = bandGHz >= 6 ? -4 : bandGHz >= 5 ? -2 : 0
  return Math.max(8, 14 + (txDbm - 15) * 0.6 + bandAdj)
}

function apTypicalCellRadiusPx(
  fw: number,
  widthM: number,
  bandGHz: number,
  txDbm: number,
): number {
  const mPerPx = widthM / Math.max(1, fw)
  return apTypicalCellRadiusM(bandGHz, txDbm) / mPerPx
}

/** Smooth RF field for any heatmap layer (wall-aware where applicable). */
export function drawCoverageCircles(
  ctx: Ctx,
  lw: number,
  lh: number,
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
  layer: HeatmapLayer = geometry.heatmapLayer,
) {
  if (aps.length === 0) return

  const layout = computeFloorDrawLayout(lw, lh, widthM, heightM)
  const cols = 144
  const rows = Math.max(72, Math.round(cols * (heightM / Math.max(1, widthM))))

  const oc = document.createElement('canvas')
  oc.width = cols
  oc.height = rows
  const octx = oc.getContext('2d', { willReadFrequently: true })
  if (!octx) return

  const img = octx.createImageData(cols, rows)
  const data = img.data
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = ((col + 0.5) / cols) * 100
      const y = ((row + 0.5) / rows) * 100
      const m = rfMetricsAtPoint(x, y, aps, geometry, widthM, heightM)
      const [r, g, b, a] = layerMetricToRgba(layer, m)
      const i = (row * cols + col) * 4
      data[i] = r
      data[i + 1] = g
      data[i + 2] = b
      data[i + 3] = a
    }
  }
  octx.putImageData(img, 0, 0)

  ctx.save()
  ctx.beginPath()
  ctx.rect(layout.x, layout.y, layout.w, layout.h)
  ctx.clip()
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.filter = layer === 'rssi' || layer === 'snr' || layer === 'quality' ? 'blur(6px)' : 'blur(8px)'
  ctx.drawImage(oc, layout.x, layout.y, layout.w, layout.h)
  ctx.filter = 'none'

  if (layer === 'rssi' || layer === 'snr' || layer === 'quality') {
    drawRfBarrierShadows(ctx, lw, lh, widthM, heightM, geometry.walls, geometry.obstacles, true)
  }
  ctx.restore()
}

function layerMetricToRgba(layer: HeatmapLayer, m: ReturnType<typeof rfMetricsAtPoint>): [number, number, number, number] {
  switch (layer) {
    case 'snr':
      return snrToRgba(m.snr)
    case 'quality':
      return qualityToRgba(m.quality)
    case 'utilization':
      return stressToRgba(m.utilization)
    case 'interference':
      return stressToRgba(m.interference)
    case 'clientDensity':
      return densityToRgba(m.clientDensity)
    default:
      return rssiToRgba(m.rssi)
  }
}

const RF_GREEN: [number, number, number] = [5, 150, 105]
const RF_AMBER: [number, number, number] = [217, 119, 6]
const RF_RED: [number, number, number] = [239, 68, 68]

/** Absolute RSSI (dBm) thresholds — aligned with coverage-hole alerts at −75 dBm. */
const RSSI_GOOD_DB = -65
const RSSI_FAIR_DB = -75

function lerpRgb(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] {
  const u = Math.max(0, Math.min(1, t))
  return [
    Math.round(a[0] + (b[0] - a[0]) * u),
    Math.round(a[1] + (b[1] - a[1]) * u),
    Math.round(a[2] + (b[2] - a[2]) * u),
  ]
}

function snrToRgba(snr: number): [number, number, number, number] {
  if (snr < 5) return [...RF_RED, 90]
  if (snr >= 25) return [...RF_GREEN, Math.round(Math.min(170, 90 + snr * 2))]
  if (snr >= 15) {
    const [r, g, b] = lerpRgb(RF_AMBER, RF_GREEN, (snr - 15) / 10)
    return [r, g, b, 130]
  }
  if (snr >= 8) return [...RF_AMBER, 120]
  return [...RF_RED, 100]
}

function qualityToRgba(quality: number): [number, number, number, number] {
  if (quality < 15) return [...RF_RED, 85]
  const alpha = Math.round(Math.min(165, 70 + quality * 0.9))
  if (quality >= 80) return [...RF_GREEN, alpha]
  if (quality >= 65) {
    const [r, g, b] = lerpRgb(RF_AMBER, RF_GREEN, (quality - 65) / 15)
    return [r, g, b, alpha]
  }
  if (quality >= 45) return [...RF_AMBER, Math.round(alpha * 0.95)]
  return [...RF_RED, Math.round(alpha * 0.85)]
}

/** Higher value = more stress (red). */
function stressToRgba(value: number): [number, number, number, number] {
  if (value < 8) return [0, 0, 0, 0]
  const t = Math.min(1, value / 100)
  const alpha = Math.round(40 + t * 120)
  if (value >= 70) return [239, 68, 68, alpha]
  if (value >= 45) return [217, 119, 6, alpha]
  return [34, 197, 94, Math.round(alpha * 0.75)]
}

function densityToRgba(value: number): [number, number, number, number] {
  if (value < 3) return [0, 0, 0, 0]
  const alpha = Math.round(Math.min(170, value * 1.6))
  if (value >= 60) return [37, 99, 235, alpha]
  if (value >= 30) return [6, 182, 212, alpha]
  return [148, 163, 184, Math.round(alpha * 0.7)]
}

/** Absolute dBm scale: green ≥ −65, amber −65…−75, red < −75 (matches RF recommendations). */
function rssiToRgba(rssi: number): [number, number, number, number] {
  if (rssi <= -94) return [0, 0, 0, 0]
  const alpha = Math.round(Math.min(165, Math.max(75, 80 + (rssi + 95) * 1.2)))
  if (rssi >= RSSI_GOOD_DB) return [...RF_GREEN, alpha]
  if (rssi >= RSSI_FAIR_DB) {
    const [r, g, b] = lerpRgb(RF_AMBER, RF_GREEN, (rssi - RSSI_FAIR_DB) / (RSSI_GOOD_DB - RSSI_FAIR_DB))
    return [r, g, b, alpha]
  }
  if (rssi >= -85) {
    const [r, g, b] = lerpRgb(RF_RED, RF_AMBER, (rssi + 85) / 10)
    return [r, g, b, Math.round(alpha * 0.95)]
  }
  return [...RF_RED, Math.round(Math.max(80, alpha * 0.9))]
}

function drawRfBarrierShadows(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  walls: FloorWall[],
  obstacles: FloorObstacle[],
  soft = false,
) {
  ctx.save()
  if (soft) {
    ctx.shadowColor = 'rgba(15,23,42,0.35)'
    ctx.shadowBlur = 8
  }
  for (const w of walls) {
    const a = pctToCanvas(w.x1, w.y1, lw, lh, widthM, heightM)
    const b = pctToCanvas(w.x2, w.y2, lw, lh, widthM, heightM)
    const att = w.attenuationDb ?? 6
    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(b.x, b.y)
    ctx.strokeStyle = `rgba(15,23,42,${Math.min(0.5, 0.15 + att / 35)})`
    ctx.lineWidth = Math.max(soft ? 6 : 4, resolveWallDimensions(w).thicknessM * 12)
    ctx.stroke()
  }
  ctx.shadowBlur = 0
  for (const o of obstacles) {
    const c = pctToCanvas(o.x, o.y, lw, lh, widthM, heightM)
    const w = (o.w / 100) * c.fw
    const h = (o.h / 100) * c.fh
    ctx.fillStyle = `rgba(15,23,42,${Math.min(0.35, 0.12 + (o.attenuationDb ?? 12) / 40)})`
    ctx.fillRect(c.x - w / 2, c.y - h / 2, w, h)
  }
  ctx.restore()
}

export function drawHeatmapLayer(
  ctx: Ctx,
  lw: number,
  lh: number,
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
  _grid: HeatmapCell[],
  _cols: number,
  _rows: number,
) {
  drawCoverageCircles(ctx, lw, lh, aps, geometry, widthM, heightM, geometry.heatmapLayer)
}

export function drawHeatmapGrid(
  ctx: Ctx,
  lw: number,
  lh: number,
  grid: HeatmapCell[],
  layer: HeatmapLayer,
  cols: number,
  rows: number,
  alphaScale = 1,
) {
  const pad = FLOOR_PAD
  const fw = lw - pad * 2
  const fh = lh - pad * 2
  ctx.save()
  ctx.beginPath()
  ctx.rect(pad, pad, fw, fh)
  ctx.clip()
  const cellW = fw / cols
  const cellH = fh / rows
  for (const cell of grid) {
    const x = pad + cell.col * cellW
    const y = pad + cell.row * cellH
    ctx.fillStyle = heatmapColor(layer, cell, alphaScale)
    ctx.fillRect(x, y, cellW + 1, cellH + 1)
  }
  ctx.restore()
}

export function drawAp(
  ctx: Ctx,
  lw: number,
  lh: number,
  ap: PlacedAp,
  selected: boolean,
  widthM = 40,
  heightM = 30,
  bandGHz = 5,
) {
  const { x: ax, y: ay, fw: floorW } = pctToCanvas(ap.x, ap.y, lw, lh, widthM, heightM)
  const mount = normalizeMountType(ap.mount)
  const ring = ap.rssiZone === 'strong' ? '#22c55e' : ap.rssiZone === 'medium' ? '#f59e0b' : '#ef4444'
  const tx = ap.txPowerDbm ?? 15
  const mountScale = mount === 'desk' ? 0.72 : mount === 'outdoor' ? 1.12 : mount === 'wall' ? 0.92 : 1
  const coverageR = apTypicalCellRadiusPx(floorW, widthM, bandGHz, tx) * mountScale

  if (mount === 'wall') {
    const orient = ((ap.mountOrientationDeg ?? 0) * Math.PI) / 180
    const beamLen = Math.min(coverageR * 0.85, 48)
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(ax, ay)
    ctx.lineTo(ax + Math.cos(orient) * beamLen, ay + Math.sin(orient) * beamLen)
    ctx.strokeStyle = 'rgba(245,158,11,0.85)'
    ctx.lineWidth = 3
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(ax, ay)
    ctx.arc(ax, ay, beamLen, orient - 0.45, orient + 0.45)
    ctx.closePath()
    ctx.fillStyle = 'rgba(245,158,11,0.12)'
    ctx.fill()
    ctx.restore()
  }

  ctx.beginPath()
  ctx.arc(ax, ay, coverageR, 0, Math.PI * 2)
  ctx.strokeStyle = ring
  ctx.lineWidth = 1.5
  ctx.setLineDash([5, 4])
  ctx.globalAlpha = 0.45
  ctx.stroke()
  ctx.setLineDash([])
  ctx.globalAlpha = 1

  if (selected) {
    ctx.beginPath()
    ctx.arc(ax, ay, 28, 0, Math.PI * 2)
    ctx.strokeStyle = '#2563eb'
    ctx.lineWidth = 2
    ctx.stroke()
  }

  ctx.beginPath()
  ctx.arc(ax, ay, 18, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()
  ctx.strokeStyle = ring
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.fillStyle = '#2563eb'
  ctx.font = '700 9px Inter, system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('AP', ax, ay)

  ctx.fillStyle = '#0f172a'
  ctx.font = '600 10px Inter, system-ui, sans-serif'
  ctx.textBaseline = 'top'
  ctx.textAlign = 'center'
  const label = ap.ap.length > 14 ? `${ap.ap.slice(0, 12)}…` : ap.ap
  ctx.fillText(label, ax, ay + 22)

  ctx.fillStyle = '#64748b'
  ctx.font = '500 9px Inter, system-ui, sans-serif'
  const mountTag =
    mount === 'wall'
      ? `Wall · ${ap.mountHeightM ?? defaultMountHeight(mount)}m`
      : mount === 'desk'
        ? `Desk · ${ap.mountHeightM ?? defaultMountHeight(mount)}m`
        : mount === 'outdoor'
          ? `Outdoor · ${ap.mountHeightM ?? defaultMountHeight(mount)}m`
          : `Ceiling · ${ap.mountHeightM ?? defaultMountHeight(mount)}m`
  ctx.fillText(mountTag, ax, ay + 34)

  const clients = ap.clients ?? 0
  if (clients > 0) {
    ctx.beginPath()
    ctx.arc(ax + 16, ay - 14, 9, 0, Math.PI * 2)
    ctx.fillStyle = '#2563eb'
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = '600 8px Inter, system-ui, sans-serif'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(clients), ax + 16, ay - 14)
  }

  const delX = ax + AP_DELETE_BADGE.dx
  const delY = ay + AP_DELETE_BADGE.dy
  ctx.beginPath()
  ctx.arc(delX, delY, AP_DELETE_BADGE.r, 0, Math.PI * 2)
  ctx.fillStyle = '#ef4444'
  ctx.fill()
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 1.5
  ctx.stroke()
  ctx.fillStyle = '#ffffff'
  ctx.font = '700 11px Inter, system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('−', delX, delY - 0.5)

  ctx.textAlign = 'left'
}

export function drawInfraDevice(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  d: FloorDevice,
  selected: boolean,
) {
  const { x: ax, y: ay } = pctToCanvas(d.x, d.y, lw, lh, widthM, heightM)
  const color = deviceStatusColor(d.status)
  ctx.fillStyle = '#ffffff'
  ctx.strokeStyle = selected ? '#2563eb' : color
  ctx.lineWidth = selected ? 2.5 : 2
  ctx.beginPath()
  ctx.rect(ax - 14, ay - 14, 28, 28)
  ctx.fill()
  ctx.stroke()
  ctx.fillStyle = color
  ctx.font = '700 8px Inter, system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(d.type.slice(0, 2).toUpperCase(), ax, ay)
  ctx.fillStyle = '#334155'
  ctx.font = '600 9px Inter, system-ui, sans-serif'
  ctx.textBaseline = 'top'
  ctx.fillText(d.name.length > 12 ? `${d.name.slice(0, 10)}…` : d.name, ax, ay + 18)
  ctx.textAlign = 'left'
}

export function drawClients(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  clients: FloorClientDot[],
  options?: {
    aps?: PlacedAp[]
    selectedApName?: string | null
    selectedClientId?: string | null
  },
) {
  const apByName = new Map((options?.aps ?? []).map(ap => [ap.ap, ap]))
  const selectedAp = options?.selectedApName ?? null
  const selectedClientId = options?.selectedClientId ?? null

  for (const c of clients) {
    const { x, y } = pctToCanvas(c.x, c.y, lw, lh, widthM, heightM)
    const ap = apByName.get(c.ap)
    const isSelected = c.id === selectedClientId
    const isOnSelectedAp = selectedAp != null && c.ap === selectedAp
    const highlight = isSelected || isOnSelectedAp

    if (ap && (highlight || clients.length <= 48)) {
      const apPt = pctToCanvas(ap.x, ap.y, lw, lh, widthM, heightM)
      ctx.save()
      ctx.setLineDash([3, 3])
      ctx.strokeStyle = highlight ? 'rgba(37,99,235,0.55)' : 'rgba(148,163,184,0.35)'
      ctx.lineWidth = highlight ? 1.5 : 1
      ctx.beginPath()
      ctx.moveTo(apPt.x, apPt.y)
      ctx.lineTo(x, y)
      ctx.stroke()
      ctx.restore()
    }

    const color = c.sticky ? '#f59e0b' : c.rssi >= -65 ? '#10b981' : c.rssi >= -75 ? '#eab308' : '#ef4444'

    ctx.beginPath()
    ctx.arc(x, y, highlight ? 6 : 5, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = isSelected ? '#2563eb' : '#ffffff'
    ctx.lineWidth = isSelected ? 2.5 : 1.5
    ctx.stroke()

    const label = c.label ?? c.id.slice(-6)
    ctx.font = '600 8px Inter, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.fillStyle = '#0f172a'
    ctx.fillText(label.length > 10 ? `${label.slice(0, 9)}…` : label, x, y - 8)

    ctx.font = '700 8px Inter, system-ui, sans-serif'
    ctx.textBaseline = 'top'
    ctx.fillStyle = color
    ctx.fillText(`${Math.round(c.rssi)} dBm`, x, y + 8)

    if (c.sticky) {
      ctx.font = '600 7px Inter, system-ui, sans-serif'
      ctx.fillStyle = '#b45309'
      ctx.fillText('sticky', x, y + 18)
    }
  }
}

export function drawAlarms(ctx: Ctx, lw: number, lh: number, widthM: number, heightM: number, alarms: FloorAlarmDot[]) {
  for (const a of alarms) {
    const { x, y } = pctToCanvas(a.x, a.y, lw, lh, widthM, heightM)
    const color = a.severity === 'CRITICAL' ? '#dc2626' : a.severity === 'HIGH' ? '#f97316' : '#eab308'
    ctx.beginPath()
    ctx.arc(x, y, 8, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2
    ctx.stroke()
  }
}

export function buildHeatmap(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
) {
  return computeHeatmapGrid(aps, geometry, widthM, heightM, RF_GRID_COLS, RF_GRID_ROWS)
}

/** Ghost marker for a suggested AP placement (recommendation preview). */
export function drawSuggestedAp(
  ctx: Ctx,
  lw: number,
  lh: number,
  xPct: number,
  yPct: number,
  widthM: number,
  heightM: number,
  label = 'Suggested AP',
) {
  const { x: ax, y: ay } = pctToCanvas(xPct, yPct, lw, lh, widthM, heightM)
  ctx.save()
  ctx.setLineDash([4, 4])
  ctx.beginPath()
  ctx.arc(ax, ay, 22, 0, Math.PI * 2)
  ctx.strokeStyle = 'rgba(16,185,129,0.85)'
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.setLineDash([])
  ctx.beginPath()
  ctx.arc(ax, ay, 14, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(16,185,129,0.2)'
  ctx.fill()
  ctx.strokeStyle = '#10b981'
  ctx.lineWidth = 1.5
  ctx.stroke()
  ctx.fillStyle = '#059669'
  ctx.font = '700 8px Inter, system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('+', ax, ay)
  ctx.fillStyle = '#047857'
  ctx.font = '600 9px Inter, system-ui, sans-serif'
  ctx.textBaseline = 'top'
  ctx.fillText(label.length > 16 ? `${label.slice(0, 14)}…` : label, ax, ay + 18)
  ctx.restore()
}

export function drawRoomPreview(
  ctx: Ctx,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) {
  const x = Math.min(x1, x2)
  const y = Math.min(y1, y2)
  const w = Math.abs(x2 - x1)
  const h = Math.abs(y2 - y1)
  const p = pctToCanvas(x, y, lw, lh, widthM, heightM)
  const pw = (w / 100) * p.fw
  const ph = (h / 100) * p.fh
  ctx.fillStyle = 'rgba(59,130,246,0.12)'
  ctx.fillRect(p.x, p.y, pw, ph)
  ctx.strokeStyle = '#2563eb'
  ctx.setLineDash([5, 4])
  ctx.strokeRect(p.x, p.y, pw, ph)
  ctx.setLineDash([])
}
